import { useQuery } from '@apollo/client';
import { AddCircle, FontDownload } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useDeleteFont } from '~/api/fonts';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { FontsOrderBy } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewFontDialog } from '../components';
import { FontListDocument, type FontList__Font as Font } from '../queries/list.generated';
import { Table } from './components';

export const FontList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { orderBy } = useOrderByParams<FontsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading, error } = useQuery(FontListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.fonts.pageInfo),
  });

  const [fontToDelete, setFontToDelete] = useState<Font | null>(null);
  const [deleteFont] = useDeleteFont();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [newFontOpen, setNewFontOpen] = useState(false);

  const onDelete = useCallback(
    async (font: Font) => {
      setFontToDelete(font);
      if (!(await confirmDelete())) return;
      void deleteFont({ variables: { input: { id: font.id } } });
    },
    [confirmDelete, deleteFont],
  );

  const fonts = useMemo(() => data?.network?.fonts.nodes ?? [], [data]);
  const totalCount = useTotalCount(data?.network?.fonts.pageInfo.nodesCount);

  // Load fonts for the current page only
  useEffect(() => {
    if (loading || fonts.length === 0) return;

    // Load each font on the current page
    fonts.forEach((font) => {
      const fontFace = new FontFace(font.name, `url(${font.file.uri})`);
      document.fonts.add(fontFace);
      void fontFace.load().catch(() => {
        // Silently ignore font loading errors (e.g., for test data with fake URIs)
      });
    });
  }, [fonts, loading]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Toolbar
        actions={
          <Button
            color="primary"
            onClick={() => setNewFontOpen(true)}
            startIcon={<AddCircle />}
            variant="contained"
          >
            {t('pages.fonts.actions.new_font')}
          </Button>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<FontDownload />}
        titleText={t('pages.fonts.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.fonts.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count selectedCount={0} totalCount={totalCount} thing="pages.fonts.thing_plural" />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table fonts={fonts} loading={loading} onDelete={onDelete} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      {newFontOpen && <NewFontDialog fullWidth onClose={() => setNewFontOpen(false)} open />}
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="pages.fonts.delete_dialog.prompt"
            values={{ fontName: fontToDelete?.name }}
            components={{ b: <strong /> }}
          />
        }
        title={t('pages.fonts.delete_dialog.title')}
      />
    </>
  );
};
